import React, { useEffect, useState } from 'react';
import { Button, CardBody, Col, Form, Label, Modal, Row } from 'reactstrap';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { loadBDNFileUploadList } from 'VesselMaster/vesselMaster.hooks';
import { errorToast, successToast } from 'Components/Toasts';
import { apiMedia } from 'global/api.global';
import Dropzone from 'react-dropzone';
import { BDNConstant } from 'shared/constants';
import { queryClient } from 'react-query/queryClient';

const PendingBDNFiles = () => {
    /** State variables */
    const { VesselID } = useSelector((state: RootState) => state.Reporting);
    const [modalState, setModalState] = useState(false);
    const [fileName, setFileName] = useState('');
    const [fileObject, setFileObject] = useState<any>({});
    const [pendingFiles, setPendingFiles] = useState<Array<any>>([]);
    /** State variables end */

    /** 
     * Fetches the list of pending BDN files for a specific vessel. 
     * Uses the `useQuery` hook to load BDN files associated with the selected vessel.
     */
    const { data: fileUploadingObj, isLoading: fileUploadingLoading, isError: fileUploadingError } = useQuery(
        [queryKeyes.vessel.BDNFileUploadList.key, VesselID],
        async () => await loadBDNFileUploadList(VesselID),
        { staleTime: Infinity }
    );

    /**
     * Adds a CSS class to the document's body to handle modal styling.
     */
    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }

    /**
     * Toggles the state of the modal and applies the necessary body styling.
     */
    function tog_backdrop() {
        setModalState(!modalState);
        removeBodyCss();
    }

    /**
     * Sets the modal state to open and stores the file name and object for uploading.
     * 
     * @param file - The file object associated with the selected BDN entry.
     */
    const handleUploadButtonClick = (file: any) => {
        setModalState(!modalState);
        setFileName(file.bdn_identification_number);
        setFileObject(file);
    }

    /**
     * Handles accepted files from the dropzone. Sets the file object for uploading.
     * 
     * @param files - List of accepted files from the dropzone.
     */
    const handleAcceptedFiles = (files: any) => {
        fileObject.bdn_file = files[0];
        fileObject.status = 'Uploaded';
    }

    /**
     * Filters pending BDN files when `fileUploadingObj` or `VesselID` changes.
     */
    useEffect(() => {
        if (fileUploadingObj) {
            const pending = fileUploadingObj.filter((file: any) => file.status === BDNConstant.PENDING);
            setPendingFiles(pending);
        }
    }, [fileUploadingObj]);

    /**
     * Handles the BDN file upload operation by sending a PUT request.
     * Shows a success or error toast depending on the result of the operation.
     */
    const handleUpload = () => {
        apiMedia.put(`bdn_file_upload/${fileObject.id}/`, fileObject)
            .then(res => {
                if (res.status === 200 || res.status === 201) {
                    queryClient.invalidateQueries(
                        queryKeyes.vessel.BDNFileUploadList.key
                    );
                    setModalState(!modalState);
                    successToast("File uploaded successfully!");
                } else {
                    errorToast("Unexpected response, please try again.");
                }
            })
            .catch(err => {
                if (err.response && [400, 500].includes(err.response.status)) {
                    errorToast("Internal error occurred, please contact the admin");
                } else {
                    errorToast("Failed to upload the file.");
                }
            });
    };
    if (fileUploadingLoading) return <div>Loading...</div>;
    if (fileUploadingError) return <div>Error loading files!</div>;
    return (
        <>
            <CardBody className="px-0 py-0 pb-0 mt-2">
                <div className="table-responsive">
                    <table className="table mb-0">
                        <thead className="table-light">
                            <tr>
                                <th className="p-2 align-middle sr-no-width">#</th>
                                <th className="p-2 align-middle">Date & Time(UTC)</th>
                                <th className="p-2 align-middle">Name Of Report</th>
                                <th className="p-2 align-middle">BDN No.</th>
                                <th className="p-2 align-middle">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingFiles.length > 0 ? (
                                pendingFiles.map((file: any, index: number) => (
                                    <tr key={index}>
                                        <td className="p-2 align-middle text-center">{index + 1}</td>
                                        <td className="p-2 align-middle">{new Date(file.reporting_date).toUTCString()}</td>
                                        <td className="p-2 align-middle">{file.name_of_report}</td>
                                        <td className="p-2 align-middle">{file.bdn_identification_number || "N/A"}</td>
                                        <td className="p-2 align-middle">
                                            <Button
                                                type="button"
                                                className='btn-sm'
                                                color="primary"
                                                onClick={() => handleUploadButtonClick(file)}>
                                                Upload
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="p-2 align-middle text-center">
                                        No pending files to upload.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </CardBody>
            <Modal
                size='md'
                isOpen={modalState}
                toggle={() => tog_backdrop()}
                backdrop={"static"}
                id="staticBackdrop"
            >
                <div className="modal-header">
                    <h5 className="modal-title" id="staticBackdropLabel">
                        Upload {fileName}
                    </h5>
                    <button
                        onClick={() => setModalState(false)}
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div className="modal-body">
                    <Form className="needs-validation"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleUpload();
                            return false;
                        }}>
                        <Dropzone
                            onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <div className="dropzone custom-dropzone mt-3" >
                                    <div className="dz-message needsclick font_16" {...getRootProps()}>
                                        <input name={`file[]`} {...getInputProps()} />
                                        <div className="cloud-icon">
                                            <i className="display-6 text-muted bx bx-cloud-upload" />
                                        </div>
                                        <Label className='mb-3'>Click here to upload.</Label>
                                    </div>
                                </div>
                            )}
                        </Dropzone>
                        <Row>
                            <Col>
                                <div className="mt-3 mb-5">
                                    <Button type="submit" color='primary' className='pos-end'>Save</Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </Modal>
        </>
    );
};

export default PendingBDNFiles;